import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { Box, Divider, IconButton, Paper, TextField, Typography } from '@weave-mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import MDEditor from '@uiw/react-md-editor';
import '@uiw/react-md-editor/markdown-editor.css';
import { usePatchSkill, useSlackChannels } from '../../api/hooks';
import { useResizableWidth } from '../../hooks/useResizableWidth';
import { SLACK_CHANNEL_SKILL_TYPES } from '../../api/types';
import { ACCENT_COLORS } from '../../utils/accentColors';
import SlackChannelPicker from './SlackChannelPicker';
const TYPE_LABEL = {
    'task-from-slack-app': 'Task from Slack app',
    'task-from-github-app': 'Task from GitHub app',
    'normal-task': 'Normal task',
    'task-from-slack-message': 'Slack — Auto-triage new messages',
};
const TYPE_ACCENT = {
    'task-from-slack-app': ACCENT_COLORS.purple,
    'task-from-github-app': ACCENT_COLORS.blue,
    'normal-task': ACCENT_COLORS.green,
    'task-from-slack-message': ACCENT_COLORS.orange,
};
/** Skill detail panel — fields are directly editable inline; each one autosaves via PATCH
 * on blur (or on MDEditor's textarea blur for Instructions) when its value actually changed. */
export default function SkillSidebar({ skill, onClose }) {
    const { mutate: patchSkill } = usePatchSkill();
    const { width, startResizing } = useResizableWidth({ defaultWidth: 720, minWidth: 480, maxWidth: 1200 });
    const [name, setName] = useState(skill.name);
    const [description, setDescription] = useState(skill.description ?? '');
    const [channelIds, setChannelIds] = useState(skill.channelIds ?? (skill.channelId ? [skill.channelId] : []));
    const [isEditingChannels, setIsEditingChannels] = useState(false);
    const { data: slackChannelsData } = useSlackChannels();
    const [repoName, setRepoName] = useState(skill.repoName ?? '');
    const [instructions, setInstructions] = useState(skill.instructions);
    const [sourceUrl, setSourceUrl] = useState(skill.sourceUrl ?? '');
    const [mcpServers, setMcpServers] = useState((skill.mcpServers ?? []).join(', '));
    useEffect(() => {
        function handleKeyDown(e) {
            if (e.key === 'Escape')
                onClose();
        }
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [onClose]);
    function saveIfChanged(field, value) {
        const current = {
            name: skill.name,
            description: skill.description ?? '',
            repoName: skill.repoName ?? '',
            instructions: skill.instructions,
            sourceUrl: skill.sourceUrl ?? '',
        }[field];
        if (value === current)
            return;
        if (field === 'name' && !value.trim())
            return;
        if (field === 'instructions' && !value.trim())
            return;
        if (field === 'sourceUrl' && !value.trim())
            return;
        patchSkill({ skillId: skill.skillId, input: { [field]: value } });
    }
    function saveMcpServersIfChanged(value) {
        const parsed = value.split(',').map((s) => s.trim()).filter(Boolean);
        const current = skill.mcpServers ?? [];
        if (parsed.length === current.length && parsed.every((s, i) => s === current[i]))
            return;
        patchSkill({ skillId: skill.skillId, input: { mcpServers: parsed } });
    }
    function saveChannelIdsIfChanged(value) {
        const current = skill.channelIds ?? (skill.channelId ? [skill.channelId] : []);
        if (value.length === current.length && value.every((v, i) => v === current[i]))
            return;
        patchSkill({ skillId: skill.skillId, input: { channelIds: value } });
    }
    return (_jsxs(Paper, { elevation: 0, sx: {
            width,
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            borderRadius: '10px',
            border: '1px solid rgba(0,0,0,0.08)',
            maxHeight: 'calc(100vh - 160px)',
            position: 'sticky',
            top: 0,
            // `position: sticky` unconditionally creates a new stacking context, which traps
            // MDEditor's fullscreen overlay (position: fixed, z-index: 99999) inside it for
            // painting purposes even though it's laid out relative to the viewport — without
            // this, the Layout's AppBar/Drawer (zIndex 1200/1201) paint on top of it. Must
            // exceed both.
            zIndex: (theme) => theme.zIndex.modal,
        }, children: [_jsx(Box, { onMouseDown: startResizing, role: "separator", "aria-orientation": "vertical", "aria-label": "Resize skill panel", sx: {
                    position: 'absolute',
                    left: -3,
                    top: 0,
                    bottom: 0,
                    width: '6px',
                    cursor: 'col-resize',
                    zIndex: 1,
                    '&:hover': { bgcolor: 'primary.main', opacity: 0.3 },
                } }), _jsxs(Box, { sx: {
                    px: 2,
                    pt: 2,
                    pb: 1.5,
                    borderBottom: '1px solid rgba(0,0,0,0.06)',
                    bgcolor: 'rgba(255,255,255,0.8)',
                }, children: [_jsxs(Box, { sx: { display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 0.75 }, children: [_jsx(Typography, { sx: {
                                    fontFamily: '"JetBrains Mono", monospace',
                                    fontSize: '0.58rem',
                                    color: 'text.secondary',
                                    letterSpacing: '0.02em',
                                    flex: 1,
                                    pt: 0.25,
                                }, children: skill.skillId }), _jsx(IconButton, { onClick: onClose, size: "small", sx: { mt: -0.5, mr: -0.75, color: 'text.secondary' }, children: _jsx(CloseIcon, { sx: { fontSize: '1rem' } }) })] }), _jsx(TextField, { value: name, onChange: (e) => setName(e.target.value), onBlur: () => saveIfChanged('name', name), variant: "standard", fullWidth: true, sx: { '& .MuiInputBase-input': { fontFamily: '"DM Sans", sans-serif', fontWeight: 700, fontSize: '0.95rem' } } })] }), _jsxs(Box, { sx: { flex: 1, overflowY: 'auto' }, children: [_jsxs(Box, { sx: { px: 2, py: 1.5 }, children: [_jsx(Typography, { sx: {
                                    fontFamily: '"Syne", sans-serif',
                                    fontSize: '0.6rem',
                                    fontWeight: 700,
                                    letterSpacing: '0.08em',
                                    textTransform: 'uppercase',
                                    color: 'text.secondary',
                                    mb: 1,
                                }, children: "Details" }), _jsx(Box, { sx: {
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    px: '7px',
                                    py: '3px',
                                    borderRadius: '5px',
                                    bgcolor: `${TYPE_ACCENT[skill.type]}14`,
                                    border: `1px solid ${TYPE_ACCENT[skill.type]}33`,
                                    mb: 1.25,
                                }, children: _jsx(Typography, { sx: { fontSize: '0.6rem', fontWeight: 700, color: TYPE_ACCENT[skill.type], letterSpacing: '0.06em', textTransform: 'uppercase' }, children: TYPE_LABEL[skill.type] }) }), _jsx(TextField, { label: "Description", value: description, onChange: (e) => setDescription(e.target.value), onBlur: () => saveIfChanged('description', description), fullWidth: true, size: "small", sx: { mb: 1.25 } }), SLACK_CHANNEL_SKILL_TYPES.includes(skill.type) && (_jsx(Box, { sx: { mb: 1.25 }, children: isEditingChannels ? (_jsxs(Box, { sx: { display: 'flex', alignItems: 'flex-start', gap: 0.5 }, children: [_jsx(Box, { sx: { flex: 1 }, children: _jsx(SlackChannelPicker, { value: channelIds, onChange: (next) => {
                                                    setChannelIds(next);
                                                    saveChannelIdsIfChanged(next);
                                                } }) }), _jsx(IconButton, { "aria-label": "Done editing Slack channels", size: "small", onClick: () => setIsEditingChannels(false), sx: { mt: 0.5 }, children: _jsx(CheckIcon, { sx: { fontSize: '1rem' } }) })] })) : (_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 0.5 }, children: [_jsxs(Box, { sx: { flex: 1 }, children: [_jsx(Typography, { sx: { fontSize: '0.7rem', color: 'text.secondary', mb: 0.25 }, children: "Slack channels" }), _jsx(Typography, { sx: { fontSize: '0.8rem' }, children: channelIds.length === 0
                                                        ? 'No channels configured'
                                                        : channelIds
                                                            .map((id) => {
                                                            const name = slackChannelsData?.results.find((c) => c.channelId === id)?.name;
                                                            return name ? `${name} (${id})` : id;
                                                        })
                                                            .join(', ') })] }), _jsx(IconButton, { "aria-label": "Edit Slack channels", size: "small", onClick: () => setIsEditingChannels(true), children: _jsx(EditIcon, { sx: { fontSize: '0.9rem' } }) })] })) })), skill.type === 'task-from-github-app' && (_jsx(TextField, { label: "Repository name", value: repoName, onChange: (e) => setRepoName(e.target.value), onBlur: () => saveIfChanged('repoName', repoName), helperText: "Optional \u2014 routes tasks from this repo to this skill", fullWidth: true, size: "small" })), _jsx(TextField, { label: "MCP servers", value: mcpServers, onChange: (e) => setMcpServers(e.target.value), onBlur: () => saveMcpServersIfChanged(mcpServers), helperText: "Comma-separated MCP server names this skill needs. Empty = none. Once set here, there is no UI control to revert to the full default set \u2014 leave this untouched if you want to preserve that.", fullWidth: true, size: "small", sx: { mt: 1.25 } })] }), _jsx(Divider, { sx: { mx: 2 } }), _jsxs(Box, { sx: { px: 2, py: 1.5 }, children: [_jsx(Typography, { sx: {
                                    fontFamily: '"Syne", sans-serif',
                                    fontSize: '0.6rem',
                                    fontWeight: 700,
                                    letterSpacing: '0.08em',
                                    textTransform: 'uppercase',
                                    color: 'text.secondary',
                                    mb: 1,
                                }, children: "Instructions" }), skill.sourceUrl !== undefined ? (_jsx(TextField, { label: "Skill source URL", value: sourceUrl, onChange: (e) => setSourceUrl(e.target.value), onBlur: () => saveIfChanged('sourceUrl', sourceUrl), helperText: "Prefer a pinned SHA or tag \u2014 branch refs may drift between task runs.", fullWidth: true, size: "small" })) : (_jsx("div", { "data-color-mode": "light", children: _jsx(MDEditor, { value: instructions, onChange: (value) => setInstructions(value ?? ''), height: 560, preview: "live", textareaProps: { id: 'skill-sidebar-instructions', onBlur: () => saveIfChanged('instructions', instructions) } }) }))] })] }), _jsx(Box, { sx: { px: 2, py: 1.25, borderTop: '1px solid rgba(0,0,0,0.06)', bgcolor: 'rgba(0,0,0,0.02)' }, children: _jsxs(Typography, { sx: { fontFamily: '"JetBrains Mono", monospace', fontSize: '0.58rem', color: 'text.secondary', lineHeight: 1.5 }, children: ["Created ", new Date(skill.createdAt).toLocaleString(), _jsx("br", {}), "Updated ", new Date(skill.updatedAt).toLocaleString()] }) })] }));
}
